import { supabase } from "@/integrations/supabase/client";

/**
 * Lightweight telemetry for the onboarding / start-flow.
 * Uses the existing `activity_logs` table – fire-and-forget, never blocks UI.
 */

export type StartFlowEvent =
  | "start_flow_opened"
  | "description_submitted"
  | "classification_received"
  | "classification_fallback"
  | "area_selected"
  | "subtype_selected"
  | "classification_confirmed"
  | "classification_overridden"
  | "situation_submitted"
  | "phase1_start_success"
  | "phase1_start_fail"
  | "start_flow_abandoned"
  | "question_displayed"
  | "phase1_first_answer"
  | "phase1_answer_rewrite"
  | "phase1_dropoff"
  | "intake_submitted"
  | "intake_extracted"
  | "intake_mapped"
  | "intake_extraction_failed"
  | "intake_field_edited"
  | "intake_field_confirmed"
  | "intake_classic_mode_used"
  | "intake_resolution_started"
  | "intake_resolution_completed"
  | "intake_file_uploaded"
  | "intake_file_parse_success"
  | "intake_file_parse_failed"
  | "intake_chunks_relevant_count"
  | "intake_fields_from_document_count"
  | "missing_field_question_shown"
  | "field_resolved"
  | "field_skipped_attempted"
  | "answer_validation_failed"
  | "resolution_complete"
  | "resolution_dropoff"
  | "intake_canonicalization_failed"
  | "intake_store_write_succeeded"
  | "intake_switched_to_classic_mode"
  | "intake_field_accepted";

interface TelemetryPayload {
  event: StartFlowEvent;
  workspaceId: string;
  metadata?: Record<string, unknown>;
}

/**
 * Log a start-flow telemetry event.
 * Non-blocking – errors are silently swallowed so UI is never affected.
 */
export function logStartFlowEvent({ event, workspaceId, metadata = {} }: TelemetryPayload) {
  // Fire-and-forget
  (async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.from("activity_logs").insert({
        workspace_id: workspaceId,
        user_id: user.id,
        activity_type: event,
        entity_type: "start_flow",
        metadata: {
          ...metadata,
          timestamp: new Date().toISOString(),
        },
      });
    } catch {
      // Telemetry must never crash the UI
    }
  })();
}
